import { Injectable } from '@angular/core';
import { featureFlagIsEnabled } from '@weavix/domain/src/feature-flag/flag-is-enabled';
import { autorun } from 'mobx';
import { facilitiesContext } from 'models-mobx/facilities-store/facilities-store';
import { FeatureFlag, featureFlagsContext } from 'models-mobx/feature-flags-store/feature-flags-store';
import { Feature, featuresContext } from 'models-mobx/feature-flags-store/features-store';
import { MyProfileStore } from 'models-mobx/my-profile-store/my-profile-store';
import { distinctUntilChanged, from, Observable, switchMap } from 'rxjs';
import { ProfileService } from './profile.service';
import { AccountService } from './account.service';
import { getRootStore } from 'models-mobx/root-store/root-store';
import { FeatureId, Feature as FeatureInterface } from '@weavix/models/src/features/feature';
import { mapMobx } from '@weavix/mobx/src/rxjs-interop';
import { HttpService } from './http.service';

@Injectable({
    providedIn: 'root',
})
export class FeatureFlagService {
    constructor(
        private readonly httpService: HttpService,
        private readonly accountService: AccountService,
        private readonly profileService: ProfileService,
    ) {}

    /**
     * Initialize the feature flags and features.  Its alright to call this multiple times as it will only initialize once.
     */
    private async initialize() {
        const accountId = this.accountService.getAccountId();
        const profile = await this.profileService.getUserProfile(null);
        MyProfileStore.initialize(profile as any, accountId);
        await Promise.all([
            getRootStore().featuresStore.loaded,
            getRootStore().featureFlagsStore.loaded,
            getRootStore().facilitiesStore.loaded,
            getRootStore().facilityPeopleStore.loaded,
        ]);
    }

    /**
     * True if the feature flag is enabled for the current user - considers a flag enabled on
     * *any* of the user's facilities as enabled overall, not just an account-wide setting. Use
     * `isFacilityFeatureFlagEnabled` instead when only a specific facility's (or the account's)
     * setting should count.
     */
    async isFeatureFlagEnabled(featureId: FeatureId, defaultValue: boolean = false): Promise<boolean> {
        await this.initialize();
        const facilities = facilitiesContext.get().getAll();
        const facilityIds = (facilities ?? []).map(x => x.id);
        const feature = featuresContext.get().get(featureId);
        const featureFlags = featureFlagsContext.get().getAll((x: FeatureFlag) => x.featureId === featureId && x.enabled !== undefined);

        return featureFlagIsEnabled(featureId, feature, featureFlags, facilityIds, defaultValue).enabled;
    }

    /**
     * Like {@link isFeatureFlagEnabled}, but a failed read is logged and counts as off: for UI that should hide a feature
     * it cannot confirm rather than fail to load.
     */
    async isFeatureFlagEnabledOrOff(featureId: FeatureId): Promise<boolean> {
        try {
            return await this.isFeatureFlagEnabled(featureId);
        } catch (e) {
            console.error(`Failed to read the ${featureId} feature flag`, e);
            return false;
        }
    }

    /**
     * True if the feature flag is enabled, in the provided account's site(s).
     */
    async isFacilityFeatureFlagEnabled(featureId: FeatureId, accountId: string, facilityIds: string[] | string, defaultValue: boolean = false): Promise<boolean> {
        facilityIds = Array.isArray(facilityIds) ? facilityIds : [facilityIds];

        await this.initialize();
        const feature = featuresContext.get().get(featureId);
        const featureFlags = featureFlagsContext.get().getAll(x => x.featureId === featureId && x.enabled !== undefined && x.accountId === accountId);

        return featureFlagIsEnabled(featureId, feature, featureFlags, facilityIds, defaultValue).enabled;
    }

    /**
     * Gets an Observable of all features.
     */
    getFeatures$() {
        return from(this.initialize())
        .pipe(
            switchMap(() => new Observable<FeatureInterface[]>((subscriber) => {
                const disposer = autorun(() => {
                    const features = (featuresContext.get().getAll() as Feature[]).map(x => ({
                        id: x.id as FeatureId,
                        name: x.name,
                        description: x.description,
                        defaultValue: x.defaultValue,
                        lastUpdated: x.lastUpdated,
                    }));
                    subscriber.next(features);
                });

                return () => {
                    disposer();
                };
            })),
        );
    }

    /**
     * Gets an Observable of a feature flag for the current user.
     */
    getFeatureFlag$(featureId: FeatureId, fallbackValue: boolean = false): Observable<boolean> {
        return from(this.initialize()).pipe(
            mapMobx(() => {
                const facilities = facilitiesContext.get().getAll();
                const facilityIds = (facilities ?? []).map(x => x.id);
                const feature = featuresContext.get().get(featureId);
                const featureFlags = featureFlagsContext.get().getAll(x => x.featureId === featureId);

                return featureFlagIsEnabled(featureId, feature, featureFlags, facilityIds, fallbackValue).enabled;
            }),
        );
    }

    /**
     * Gets an Observable of a feature flag, in the provided account's site(s).
     */
    getFacilityFeatureFlag$(featureId: FeatureId, accountId: string, facilityIds: string[] | string, fallbackValue: boolean = false): Observable<boolean> {
        facilityIds = Array.isArray(facilityIds) ? facilityIds : [facilityIds];
        return from(this.initialize()).pipe(
            mapMobx(() => {
                const feature = featuresContext.get().get(featureId);
                const featureFlags = featureFlagsContext.get().getAll(x => x.featureId === featureId && x.accountId === accountId);

                return featureFlagIsEnabled(featureId, feature, featureFlags, facilityIds, fallbackValue).enabled;
            }),
        );
    }

    /**
     * Emits a key that changes whenever any feature or flag does. For callers that read flags once and
     * must re-read when synced data lands after sign-in; compare with {@link getFlagsVersion}.
     */
    getFlagsVersion$(): Observable<string> {
        return from(this.initialize()).pipe(
            mapMobx(() => this.getFlagsVersion()),
            distinctUntilChanged(),
        );
    }

    /** The current {@link getFlagsVersion$} key, read as-is without waiting for the stores to load. */
    getFlagsVersion(): string {
        const features = (featuresContext.get()?.getAll() ?? []).map(x => `${x.id}=${x.defaultValue}`).sort((a, b) => a.localeCompare(b));
        const flags = (featureFlagsContext.get()?.getAll() ?? []).map(x => `${x.id}=${x.enabled}`).sort((a, b) => a.localeCompare(b));
        return `${features.join(',')}|${flags.join(',')}`;
    }

    /**
     * Reads a flag for the signed-in user from the server, across all their accounts and sites. For a session that has not
     * loaded the flag stores yet (e.g. a first passwordless login); otherwise use {@link isFeatureFlagEnabled}.
     */
    isMyFeatureFlagEnabled(featureId: FeatureId) {
        return this.httpService.get<{ enabled: boolean }>(null, `/core/features/feature-flags/${featureId}`, null);
    }

    /**
     * This should ONLY be used when logged out; otherwise, use the service.
     * This method bypasses our context stores, creating potential state inconsistencies.
     * Use isFeatureFlagEnabled() or isFacilityFeatureFlagEnabled() instead when the user is logged in.
     */
    isAccountFeatureFlagEnabled(featureId: FeatureId, accountId: string) {
        return this.httpService.get<{ enabled: boolean }>(null, `/core/features/feature-flags/${featureId}/accounts/${accountId}`, null);
    }
}
